class CodeltyImmersiveSlider extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loop = true;
    this.showDots = false;
    this.dots = [];
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('.codelty-immersive-slider__viewport');
    this.track = this.querySelector('.codelty-immersive-slider__track');
    if (!this.viewport || !this.track) return;

    this.slides = [...this.track.querySelectorAll('.codelty-immersive-slider__slide')];
    if (this.slides.length < 2) return;

    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');
    this.loop = this.dataset.loop !== 'false';
    this.showDots = this.dataset.dots === 'true';

    this.bindArrows();
    this.bindAutoplay();
    this.bindTouchContainment();

    if (this.showDots) this.buildDots();

    this.viewport.addEventListener('scroll', () => this.syncUi(), { passive: true });
    window.addEventListener('resize', () => this.syncUi());
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.syncUi();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay();
      return;
    }

    this.startAutoplay();
  }

  getStep() {
    const slide = this.slides[0];
    if (!slide) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 16;
    return slide.getBoundingClientRect().width + gap;
  }

  getCurrentIndex() {
    const step = this.getStep();
    if (!step) return 0;
    return Math.round(this.viewport.scrollLeft / step);
  }

  bindArrows() {
    this.prevBtn?.addEventListener('click', () => this.goPrev());
    this.nextBtn?.addEventListener('click', () => this.goNext());
  }

  syncUi() {
    this.syncArrows();
    this.syncDots();
  }

  syncArrows() {
    if (!this.prevBtn || !this.nextBtn) return;

    if (this.loop) {
      this.prevBtn.disabled = false;
      this.nextBtn.disabled = false;
      return;
    }

    const max = this.viewport.scrollWidth - this.viewport.clientWidth - 2;
    this.prevBtn.disabled = this.viewport.scrollLeft <= 2;
    this.nextBtn.disabled = this.viewport.scrollLeft >= max;
  }

  buildDots() {
    if (this.querySelector('[data-slider-dots]')) return;

    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'codelty-immersive-slider__dots';
    dotsWrap.setAttribute('data-slider-dots', '');
    dotsWrap.setAttribute('role', 'tablist');

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'codelty-immersive-slider__dot';
      dot.dataset.index = String(index);
      dot.setAttribute('aria-label', `Slide ${index + 1}`);
      dot.addEventListener('click', () => this.goToIndex(index));
      dotsWrap.appendChild(dot);
    });

    this.viewport.insertAdjacentElement('afterend', dotsWrap);
    this.dots = [...dotsWrap.querySelectorAll('.codelty-immersive-slider__dot')];
  }

  syncDots() {
    if (!this.dots.length) return;
    const index = this.getCurrentIndex();
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  goToIndex(index) {
    const step = this.getStep();
    this.viewport.scrollTo({
      left: step * index,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
    window.setTimeout(() => this.syncUi(), 400);
  }

  goNext() {
    const step = this.getStep();
    if (!step) return;

    const max = this.viewport.scrollWidth - this.viewport.clientWidth;

    if (this.loop && this.viewport.scrollLeft >= max - 2) {
      this.viewport.scrollTo({ left: 0, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    } else {
      this.viewport.scrollBy({ left: step, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    window.setTimeout(() => this.syncUi(), 400);
  }

  goPrev() {
    const step = this.getStep();
    if (!step) return;

    if (this.loop && this.viewport.scrollLeft <= 2) {
      this.viewport.scrollTo({ left: this.viewport.scrollWidth, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    } else {
      this.viewport.scrollBy({ left: -step, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    window.setTimeout(() => this.syncUi(), 400);
  }

  bindAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.reducedMotion) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.startAutoplay());
    });
  }

  bindTouchContainment() {
    if (!this.viewport) return;

    let startX = 0;
    let startY = 0;

    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        if (!event.touches.length) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchmove',
      (event) => {
        if (!event.touches.length) return;

        const dx = event.touches[0].clientX - startX;
        const dy = event.touches[0].clientY - startY;

        if (Math.abs(dx) <= Math.abs(dy)) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
        const maxScroll = scrollWidth - clientWidth;

        if (maxScroll <= 0) {
          event.preventDefault();
          return;
        }

        const atStart = scrollLeft <= 0;
        const atEnd = scrollLeft >= maxScroll - 1;

        if ((atStart && dx > 0) || (atEnd && dx < 0)) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
  }

  startAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.reducedMotion || document.hidden) return;

    this.stopAutoplay();
    const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.timer = window.setInterval(() => this.goNext(), speed);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('codelty-immersive-slider')) {
  customElements.define('codelty-immersive-slider', CodeltyImmersiveSlider);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('codelty-immersive-slider').forEach((slider) => {
    if (typeof slider.syncUi === 'function') slider.syncUi();
    if (typeof slider.startAutoplay === 'function') slider.startAutoplay();
  });
});
